import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card } from 'react-bootstrap';

/**
 * A heading data item that can be displayed in a DataHeader
 * 
 * @param {string} label - Label text to display
 * @param {node} value - Value to display
 * @param {string} className - Additional CSS classes
 * @param {node} children - Optional child elements to render below the value
 */
export const DataItem = ({ label, value, className = '', children }) => {
  return (
    <Col className={`text-center ${className}`}>
      <div className="text-muted mb-2">{label}</div>
      <h4 className="mb-1">{value}</h4>
      {children}
    </Col>
  );
};

DataItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  className: PropTypes.string,
  children: PropTypes.node
};

/**
 * A card component with header that displays data items in a row
 * 
 * @param {node} children - DataItem components
 * @param {string} title - Card title
 * @param {string} align - Horizontal alignment of the group (left, center, right)
 * @param {string} className - Additional CSS classes
 * @param {node} actions - Optional actions to display in the header (buttons, etc.)
 */
const DataHeader = ({ 
  children, 
  title, 
  align = 'right', 
  className = '',
  actions
}) => {
  const justifyClass = align === 'left' 
    ? 'justify-content-start' 
    : align === 'center' 
      ? 'justify-content-center' 
      : 'justify-content-end';
  
  return (
    <Card className={`mb-4 ${className}`}>
      <Card.Body>
        {title && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>{title}</Card.Title>
            {actions && <div>{actions}</div>}
          </div>
        )}
        <Row className={justifyClass}>
          {children}
        </Row>
      </Card.Body>
    </Card>
  );
};

DataHeader.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.node,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  className: PropTypes.string,
  actions: PropTypes.node
};

export default DataHeader;